// Planckian locus (Kang et al. 2002), CIE 1960 uv, Bradford adaptation (§6.3).
import { apply, diag, inv, mul, type Mat3, type Vec3 } from './matrix';

/** Kang et al. 2002 cubic spline approximation of the Planckian locus, xy. Valid 1667–25000 K. */
export function planckXy(T: number): [number, number] {
  const t = Math.min(25000, Math.max(1667, T));
  const x =
    t <= 4000
      ? -0.2661239e9 / t ** 3 - 0.2343589e6 / t ** 2 + 0.8776956e3 / t + 0.17991
      : -3.0258469e9 / t ** 3 + 2.1070379e6 / t ** 2 + 0.2226347e3 / t + 0.24039;
  const y =
    t <= 2222
      ? -1.1063814 * x ** 3 - 1.3481102 * x ** 2 + 2.18555832 * x - 0.20219683
      : t <= 4000
        ? -0.9549476 * x ** 3 - 1.37418593 * x ** 2 + 2.09137015 * x - 0.16748867
        : 3.081758 * x ** 3 - 5.8733867 * x ** 2 + 3.75112997 * x - 0.37001483;
  return [x, y];
}

export const xyToUv = (x: number, y: number): [number, number] => {
  const d = -2 * x + 12 * y + 3;
  return [(4 * x) / d, (6 * y) / d];
};
export const uvToXy = (u: number, v: number): [number, number] => {
  const d = 2 * u - 8 * v + 4;
  return [(3 * u) / d, (2 * v) / d];
};
export const xyToXYZ = (x: number, y: number): Vec3 => [x / y, 1, (1 - x - y) / y];

/**
 * Planckian white at temperature T offset by `tint` perpendicular to the locus in CIE 1960 uv.
 * `duv` > 0 moves toward green (above the locus), < 0 toward magenta.
 */
export function planckWithOffset(T: number, duv: number): [number, number] {
  const [u, v] = xyToUv(...planckXy(T));
  if (duv === 0) return uvToXy(u, v);
  // Tangent by central difference in T; the normal is the tangent rotated +90°, oriented to +v.
  const h = Math.max(1, T * 1e-3);
  const [u1, v1] = xyToUv(...planckXy(T - h));
  const [u2, v2] = xyToUv(...planckXy(T + h));
  let nx = -(v2 - v1);
  let ny = u2 - u1;
  const len = Math.hypot(nx, ny) || 1;
  nx /= len;
  ny /= len;
  if (ny < 0) {
    nx = -nx;
    ny = -ny;
  }
  return uvToXy(u + nx * duv, v + ny * duv);
}

export const BRADFORD: Mat3 = [
  [0.8951, 0.2664, -0.1614],
  [-0.7502, 1.7135, 0.0367],
  [0.0389, -0.0685, 1.0296],
];
const BRADFORD_INV = inv(BRADFORD);

/** Bradford chromatic adaptation matrix (XYZ → XYZ) from white `src` to white `dst` (both XYZ, Y = 1). */
export function bradford(src: Vec3, dst: Vec3): Mat3 {
  const s = apply(BRADFORD, src);
  const d = apply(BRADFORD, dst);
  return mul(BRADFORD_INV, mul(diag([d[0] / s[0], d[1] / s[1], d[2] / s[2]]), BRADFORD));
}

export const D65_XYZ: Vec3 = [0.95047, 1, 1.08883];
/** ICC PCS illuminant (ICC.1 §7.2.16). */
export const D50_XYZ: Vec3 = [0.9642, 1, 0.8249];
